import React, { useEffect, useState } from 'react';
import { Text as UIText } from './ui';

const WalkthroughOverlay = ({ 
  isActive = false,
  highlightedElement = null,
  message = "",
  subMessage = "",
  position = 'right',
  className = ""
}) => {
  const [elementRect, setElementRect] = useState(null);
  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);

  useEffect(() => {
    if (highlightedElement && isActive) {
      const updateRect = () => {
        const rect = highlightedElement.getBoundingClientRect();
        setElementRect({
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height
        });
        setViewportHeight(window.innerHeight);
      };

      // Initial measurement
      updateRect();

      // Update on scroll and resize
      const handleUpdate = () => {
        if (highlightedElement && isActive) {
          updateRect();
        }
      };

      window.addEventListener('scroll', handleUpdate, true);
      window.addEventListener('resize', handleUpdate);
      
      // Use ResizeObserver for more precise updates
      let resizeObserver;
      if (window.ResizeObserver) {
        resizeObserver = new ResizeObserver(handleUpdate);
        resizeObserver.observe(highlightedElement);
      }

      // Also observe document body for layout changes (like when lineage viewer opens)
      let bodyResizeObserver;
      if (window.ResizeObserver && document.body) {
        bodyResizeObserver = new ResizeObserver(handleUpdate);
        bodyResizeObserver.observe(document.body);
      }

      return () => {
        window.removeEventListener('scroll', handleUpdate, true);
        window.removeEventListener('resize', handleUpdate);
        if (resizeObserver) {
          resizeObserver.disconnect();
        }
        if (bodyResizeObserver) {
          bodyResizeObserver.disconnect();
        }
      };
    } else {
      setElementRect(null);
    }
  }, [highlightedElement, isActive]);

  if (!isActive || !elementRect) return null;

  // Calculate viewport dimensions to ensure full coverage
  const currentViewportHeight = typeof window !== 'undefined' ? window.innerHeight : viewportHeight;
  const bottomOverlayTop = elementRect.top + elementRect.height + 4;
  
  // Calculate the maximum height needed to cover the entire viewport and beyond
  // Use a very large value to ensure it covers all scrollable content
  const maxHeight = Math.max(
    currentViewportHeight - bottomOverlayTop,
    10000 // Large enough to cover any scrollable content
  );

  return (
    <>
      {/* Top overlay - blocks clicks */}
      <div 
        className="fixed z-30 pointer-events-auto"
        style={{
          top: 0,
          left: 0,
          right: 0,
          height: Math.max(0, elementRect.top - 4),
          backgroundColor: 'rgba(0, 0, 0, 0.6)'
        }}
      />
      
      {/* Bottom overlay - blocks clicks - ensure it covers to the bottom of viewport and beyond */}
      <div 
        className="fixed z-30 pointer-events-auto"
        style={{
          top: bottomOverlayTop,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)'
        }}
      />
      
      {/* Left overlay - blocks clicks */}
      <div 
        className="fixed z-30 pointer-events-auto"
        style={{
          top: Math.max(0, elementRect.top - 4),
          left: 0,
          width: Math.max(0, elementRect.left - 4),
          height: elementRect.height + 8,
          backgroundColor: 'rgba(0, 0, 0, 0.6)'
        }}
      />
      
      {/* Right overlay - blocks clicks */}
      <div 
        className="fixed z-30 pointer-events-auto"
        style={{
          top: Math.max(0, elementRect.top - 4),
          left: elementRect.left + elementRect.width + 4,
          right: 0,
          height: elementRect.height + 8,
          backgroundColor: 'rgba(0, 0, 0, 0.6)'
        }}
      />
      
      {/* Highlighted element border - positioned over the element */}
      <div 
        className="fixed pointer-events-none z-40"
        style={{
          top: elementRect.top - 4,
          left: elementRect.left - 4,
          width: elementRect.width + 8,
          height: elementRect.height + 8,
          border: '3px solid #1b263b',
          borderRadius: '0px',
          boxShadow: '0 0 0 1px white, 0 0 0 2px #1b263b',
          backgroundColor: 'transparent',
          transition: 'none'
        }}
      />
    </>
  );
};

export default WalkthroughOverlay;
